import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { DisplayedProduct } from 'src/app/pages/tunnel/common/base-complementary-product/base-complementary-product.component';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, Observable, shareReplay } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'ng-casto-complementary-product-list',
  templateUrl: './complementary-product-list.component.html',
})
export class ComplementaryProductListComponent implements AfterViewInit {
  //Input
  @Input() fetchProductsLoading: boolean = false;
  @Input() filteredProducts$: Observable<DisplayedProduct[]>;

  //Output
  @Output() onProductQuantityChange: EventEmitter<{
    product: DisplayedProduct;
    quantity: number;
  }> = new EventEmitter();

  //Inject
  private readonly destroyRef = inject(DestroyRef);

  //Consts
  private readonly PAGE_SIZE = 30;
  //Observable
  private readonly visibleCount$ = new BehaviorSubject<number>(this.PAGE_SIZE);
  protected visibleProducts$: Observable<DisplayedProduct[]>;

  //Attributes
  private ioInitialized = false;
  private io?: IntersectionObserver;
  protected imageLoadStates = new Map<string, boolean>();

  //viewChild
  private _scrollHost?: HTMLElement;

  @ViewChild('scrollHost') set scrollHostRef(el: ElementRef<HTMLElement> | undefined) {
    this._scrollHost = el?.nativeElement ?? undefined;
    if (!this.ioInitialized) {
      this.tryInitIO();
    }
  }

  private _sentinel?: HTMLDivElement;

  @ViewChild('infiniteSentinel') set infiniteSentinelRef(el: ElementRef<HTMLDivElement> | undefined) {
    const newSentinel = el?.nativeElement ?? undefined;

    // Si la sentinelle change et qu'on a déjà un observer actif
    if (this._sentinel !== newSentinel && this.io && newSentinel) {
      this.io.disconnect();
      this.io.observe(newSentinel);
    }

    this._sentinel = newSentinel;

    if (!this.ioInitialized) {
      this.tryInitIO();
    }
  }

  ngAfterViewInit(): void {
    this.visibleProducts$ = combineLatest([this.filteredProducts$, this.visibleCount$]).pipe(
      map(([items, n]) => items.slice(0, n)),
      shareReplay(1)
    );
  }

  private tryInitIO(): void {
    // Attendre que les éléments DOM soient disponibles
    if (!this._scrollHost || !this._sentinel) return;

    // Éviter les initialisations multiples
    if (this.ioInitialized) return;

    this.ioInitialized = true;

    // 1. Configuration de l'IntersectionObserver pour détecter le scroll vers le bas
    // Déclenche bumpVisible() quand le sentinel (élément en fin de liste) devient visible
    if ('IntersectionObserver' in globalThis) {
      this.io = new IntersectionObserver(
        entries => {
          if (!entries.some(e => e.isIntersecting)) return;
          this.bumpVisible();
        },
        {
          root: this._scrollHost,
          rootMargin: '800px 0px', // Précharge 800px avant d'atteindre le sentinel
          threshold: 0,
        }
      );
      this.io.observe(this._sentinel);
      this.destroyRef.onDestroy(() => this.io?.disconnect());
    }

    // 2. Réinitialisation de la pagination lors d'un changement de filtres
    // Remet visibleCount à PAGE_SIZE et scroll en haut dès que la liste filtrée change
    this.filteredProducts$
      .pipe(
        distinctUntilChanged((prev, curr) => prev.length === curr.length),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.visibleCount$.next(this.PAGE_SIZE);
        this._scrollHost?.scrollTo({ top: 0, behavior: 'instant' });
      });

    // 3. Auto-remplissage du viewport
    // À chaque changement de produits visibles, vérifie si le viewport est plein
    // Si non, incrémente visibleCount pour afficher plus de produits
    // Ce mécanisme continue récursivement jusqu'à remplir le viewport ou atteindre la limite
    combineLatest([this.visibleProducts$, this.filteredProducts$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([visible, filtered]) => {
        // Attendre le prochain cycle de rendu pour avoir les bonnes dimensions
        requestAnimationFrame(() => {
          const host = this._scrollHost;
          if (!host) return;

          const currentVisible = this.visibleCount$.getValue();
          const maxAllowed = filtered.length;
          const viewportNotFull = host.scrollHeight <= host.clientHeight;
          const canGrow = currentVisible < maxAllowed;

          // Si le viewport n'est pas rempli et qu'on peut afficher plus de produits, on bump
          if (viewportNotFull && canGrow) {
            this.bumpVisible();
          }
        });
      });

    // 4. Gestion du redimensionnement du conteneur
    // Si la fenêtre est agrandie, le viewport peut devenir vide, donc on bump si nécessaire
    if ('ResizeObserver' in globalThis) {
      const ro = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          const host = this._scrollHost;
          if (!host) return;

          const viewportNotFull = host.scrollHeight <= host.clientHeight;
          if (viewportNotFull) {
            this.bumpVisible();
          }
        });
      });
      ro.observe(this._scrollHost);
      this.destroyRef.onDestroy(() => ro.disconnect());
    }
  }

  private bumpVisible(): void {
    const current = this.visibleCount$.getValue();
    const next = current + this.PAGE_SIZE;
    if (next !== current) this.visibleCount$.next(next);
  }

  protected onImageLoad(product: DisplayedProduct) {
    this.imageLoadStates.set(product.ean, true);
  }

  protected isImageLoaded(product: DisplayedProduct): boolean {
    return this.imageLoadStates.get(product.ean) || false;
  }
}
